/**
 * Shared utilities for action resolvers
 *
 * Contains helper functions used across different resolver types.
 * All functions are pure and deterministic for testability.
 */

import { calculateDistance, normalizeDistance } from '@/lib/ideology'
import type { IdeologyVector } from '@/constants/ideology'
import type { Cohort } from '@/types'

/**
 * Calculate how effective a campaign will be for a specific cohort
 * based on ideology alignment between party and cohort.
 *
 * @param partyIdeology - The party's ideology vector
 * @param cohortIdeology - The cohort's ideology vector
 * @param cohortSalience - The cohort's issue salience weights
 * @returns Alignment factor from 0 (opposed) to 1 (perfect match)
 *
 * Formula:
 * - Calculate weighted distance using cohort's salience
 * - Convert to 0-1 alignment factor (1 = close, 0 = far)
 * - Minimum alignment of 0.1 ensures even opposed cohorts get tiny effect
 */
export function calculateAlignmentFactor(
  partyIdeology: IdeologyVector,
  cohortIdeology: IdeologyVector,
  cohortSalience: IdeologyVector
): number {
  const distance = calculateDistance(partyIdeology, cohortIdeology, cohortSalience)
  const normalized = normalizeDistance(distance)

  // Convert normalized distance (0-100 where 0=close) to alignment factor (0-1 where 1=close)
  // Use a gentler curve that doesn't punish moderate differences too harshly
  const alignmentFactor = Math.max(0.1, 1 - normalized / 100)

  return alignmentFactor
}

/**
 * Calculate diminishing returns factor for recognition gains.
 * Makes it harder to gain recognition when already well-known.
 *
 * @param currentRecognition - Current recognition level (0-100)
 * @returns Diminishing factor from 0.2 (at 100) to 1.0 (at 0)
 *
 * Formula: max(0.2, 1 - currentRecognition / 120)
 * - At 0 recognition: factor = 1.0 (full gains)
 * - At 60 recognition: factor = 0.5 (half gains)
 * - At 100+ recognition: factor = 0.2 (minimum gains)
 */
export function calculateDiminishingFactor(currentRecognition: number): number {
  return Math.max(0.2, 1 - currentRecognition / 120)
}

/**
 * Clamp a value to 0-100 range (standard game stat range)
 */
export function clampStat(value: number): number {
  return Math.max(0, Math.min(100, value))
}

/**
 * Round a value to 1 decimal place for clean display
 */
export function roundToOneDecimal(value: number): number {
  return Math.round(value * 10) / 10
}

/**
 * Generate a human-readable cohort description for result summaries.
 *
 * @param cohort - The cohort to describe
 * @returns A short description like "Young Urban High-Income"
 */
export function describeCohort(cohort: Cohort): string {
  const ageLabels: Record<string, string> = {
    youth: 'Young',
    young_adult: 'Young Adult',
    middle_aged: 'Middle-Aged',
    senior: 'Senior',
  }

  const incomeLabels: Record<string, string> = {
    low: 'Low-Income',
    middle: 'Middle-Income',
    high: 'High-Income',
  }

  const educationLabels: Record<string, string> = {
    low: 'Low-Ed',
    medium: 'Med-Ed',
    high: 'High-Ed',
  }

  const age = ageLabels[cohort.age_bracket] || cohort.age_bracket
  const income = incomeLabels[cohort.income_level] || cohort.income_level
  const education = educationLabels[cohort.education_level] || cohort.education_level

  return `${age} ${education} ${income}`
}

/**
 * Format a number as a human-readable voter count
 */
export function formatVoterCount(count: number): string {
  if (count >= 1000000) {
    return `${(count / 1000000).toFixed(1)}M`
  }
  if (count >= 1000) {
    return `${(count / 1000).toFixed(1)}K`
  }
  return count.toLocaleString()
}

/**
 * Convert a string to a deterministic number for variance calculations.
 * Used to create consistent "randomness" from action IDs.
 *
 * @param str - String to hash (typically an action ID/UUID)
 * @returns Positive integer hash value
 *
 * @example
 * const hash = hashStringToNumber('abc-123-def')
 * // Always returns the same number for the same input
 */
export function hashStringToNumber(str: string): number {
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i)
    hash = ((hash << 5) - hash) + char
    hash = hash & hash // Convert to 32-bit integer
  }
  return Math.abs(hash)
}
